/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Reports constructor template rename modal
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useEffect } from 'react';
import type { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ERequestStatus } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { Button } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { makeSelectPageReportsConstructorTranslation, makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import Modal from '@modules/modal/components/Modal';
import { ModalTitleStyled, ModalTitleTextStyled } from '@modules/modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import { stringFormatParams } from '@utils/stringFormat';

import { useLazyGetConstructorTemplatesQuery, useRemoveReportTemplateMutation } from '../store';
import type { TTemplateId } from '../types';
import { ConstructorRequestStatusModal } from './ConstructorRequestStatusModal';

interface IProps {
    title: string;
    templateId: TTemplateId;
    onCloseModal: () => void;
};

export const TemplateRemoveModal: FC<IProps> = ({ templateId, title, onCloseModal }) => {
    const dispatch = useDispatch();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const {
        start: tStartRemove,
        success: tSuccessRemove,
        failure: tFailureRemove,
    } = useSelector(makeSelectPageReportsConstructorTranslation());

    const [
        removeTemplate,
        {
            error,
            isError,
            isLoading,
            isSuccess,
        },
    ] = useRemoveReportTemplateMutation();


    const [getConstructorTemplates] = useLazyGetConstructorTemplatesQuery();

    useEffect(() => {
        if (isSuccess) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.success,
            }))
        }
    }, [dispatch, isSuccess])

    useEffect(() => {
        if (isError) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.failure,
            }))
        }
    }, [dispatch, isError]);

    const handleRemove = () => {
        removeTemplate({ templateId }).unwrap().then(() => getConstructorTemplates(0));
    };

    return (
        <Modal
            dataName='reports-template-remove-modal'
            style={{ padding: '24px' }}
            width='480px'
        >
            <Modal.Status status={EModalStatus.start}>
                <Modal.Title className='modal-popup-title__base'>
                    <ModalTitleStyled>
                        <ModalTitleTextStyled>
                            {tStartRemove.removeTemplateModalTitle}
                        </ModalTitleTextStyled>
                        <ImgStyled height='16px' src={icons.cancelSmallIcon} width='16px' onClick={onCloseModal} />
                    </ModalTitleStyled>
                </Modal.Title>
                <Loader showLoader={isLoading}>
                    <Modal.Text>
                        {stringFormatParams(tStartRemove.removeTemplateModalText, { name: title })}
                    </Modal.Text>
                    <Modal.Control className='modal-popup-control__base'>
                        <Button
                            onClick={handleRemove}>
                            {tCommon.modal.remove}
                        </Button>
                    </Modal.Control>
                </Loader>
            </Modal.Status >
            <Modal.Status status={EModalStatus.success}>
                <ConstructorRequestStatusModal
                    mode={ERequestStatus.success}
                    title={tSuccessRemove.removeTemplateModalTitle}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <ConstructorRequestStatusModal
                    mode={ERequestStatus.fail}
                    requestError={error}
                    title={tFailureRemove.removeTemplateModalTitle}
                />
            </Modal.Status>
        </Modal >
    );
};
